import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import LoginView from '../views/LoginView.vue'
import DefaultLayout from '../layout/DefaultLayout.vue'
import { useUserStore } from '@/stores/userStore' // 🌟 引入您寫好的 Store
import { ensureAccessToken } from '@/utils/authSession'
import { PermissionCode, RoleCode, disabledReportPermissions } from '@/constants/accessControl'
import { getDefaultAuthorizedRoute } from '@/utils/accessNavigation'

// 定義路由陣列
const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: DefaultLayout,
    redirect: () => getDefaultAuthorizedRoute(useUserStore().userInfo),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/DashboardView.vue'),
        meta: { title: '儀表板', icon: 'home', permission: PermissionCode.MenuCaseHome },
      },

      //經銷商使用
      //status=0
      {
        path: 'incoming/temporaryList',
        name: 'TemporaryList',
        component: () => import('../views/incoming/TemporaryList.vue'),
        meta: {
          title: '營業所暫存',
          icon: 'folder-open',
          permission: PermissionCode.MenuIncomingTemporary,
        },
      },

      //合信業務是使用
      //status=10
      {
        path: 'webincoming/WebTemporaryList',
        name: 'WebTemporaryList',
        component: () => import('../views/webincoming/WebTemporaryList.vue'),
        meta: { title: '官網暫存件', icon: 'save', permission: PermissionCode.MenuWebTemporary },
      },
      //status=11
      {
        path: 'webincoming/WebToContactList',
        name: 'WebToContactList', // 🌟 已修正重複的 name 與 path
        component: () => import('../views/webincoming/WebToContactList.vue'),
        meta: {
          title: '官網申請完成',
          icon: 'contact',
          permission: PermissionCode.MenuWebToContact,
        },
      },
      //status=12
      {
        path: 'webincoming/WebConfirmedList',
        name: 'WebConfirmedList',
        component: () => import('../views/webincoming/WebConfirmedList.vue'),
        meta: {
          title: '申請資料退回確認',
          icon: 'contact',
          permission: PermissionCode.MenuWebConfirmed,
        },
      },

      // 共用
      {
        path: 'case/form/:id?',
        name: 'CaseForm',
        component: () => import('../views/case/FormView.vue'),
        meta: {
          title: '填寫進件資料',
          hiddenInMenu: true,
          permissions: [PermissionCode.CaseRead, PermissionCode.CaseCreate],
        }, // 表單可能共用，視需求開放
      },

      //status=1
      {
        path: 'case/ReadyToCaseToDoList',
        name: 'ReadyToCaseToDoList',
        component: () => import('../views/case/ReadyToCaseToDoList.vue'),
        meta: { title: '業務按出送件', icon: 'clock', permission: PermissionCode.MenuCaseReady },
      },
      //status=2
      {
        path: 'case/AlreadyToCaseToDoList',
        name: 'AlreadyToCaseToDoList',
        component: () => import('../views/case/AlreadyToCaseToDoList.vue'),
        meta: {
          title: '已送已進件',
          icon: 'check',
          permission: PermissionCode.MenuCaseAccepted,
        },
      },

      //考慮拿掉，統計放儀表板即可，status=3
      {
        path: 'case/AlreadyToReviewToDoList',
        name: 'AlreadyToReviewToDoList',
        component: () => import('../views/case/AlreadyToReviewToDoList.vue'),
        meta: { title: '審查中', icon: 'check', permission: PermissionCode.MenuCaseReviewing },
      },

      //status=4
      {
        path: 'case/ReturnedList',
        name: 'ReturnedList',
        component: () => import('../views/case/ReturnedList.vue'),
        meta: { title: '進件退回', icon: 'reply', permission: PermissionCode.MenuCaseReturned },
      },

      //status=-1
      {
        path: 'case/canceled',
        name: 'CanceledList',
        component: () => import('../views/case/CanceledList.vue'),
        meta: { title: '作廢件', icon: 'trash', permission: PermissionCode.MenuCaseCanceled },
      },

      //status=60
      {
        path: 'case/ApprovedPendingContractList',
        name: 'ApprovedPendingContractList',
        component: () => import('../views/case/ApprovedPendingContractList.vue'), // 請換成正確的組件
        meta: {
          title: '核准(未簽約)',
          icon: 'contact',
          permission: PermissionCode.MenuCaseApprovedPendingContract,
        },
      },
      //status=64
      {
        path: 'case/RejectedList',
        name: 'RejectedList',
        component: () => import('../views/case/RejectedList.vue'), // 請換成正確的組件
        meta: { title: '婉拒', icon: 'contact', permission: PermissionCode.MenuCaseRejected },
      },
      //status=61
      {
        path: 'case/PendingSupplementList',
        name: 'PendingSupplementList',
        component: () => import('../views/case/PendingSupplementList.vue'), // 請換成正確的組件
        meta: {
          title: '待補',
          icon: 'contact',
          permission: PermissionCode.MenuCasePendingSupplement,
        },
      },
      //status=62
      {
        path: 'case/ConditionalApprovalList',
        name: 'ConditionalApprovalList',
        component: () => import('../views/case/ConditionalApprovalList.vue'), // 請換成正確的組件
        meta: {
          title: '附條件',
          icon: 'contact',
          permission: PermissionCode.MenuCaseConditional,
        },
      },
      //status=63
      {
        path: 'case/SelfCancelList',
        name: 'SelfCancelList',
        component: () => import('../views/case/SelfCancelList.vue'), // 請換成正確的組件
        meta: {
          title: '自退',
          icon: 'contact',
          permission: PermissionCode.MenuCaseSelfCanceled,
        },
      },

      //status=65
      {
        path: 'case/TransferList',
        name: 'TransferList',
        component: () => import('../views/case/TransferList.vue'),
        meta: { title: '轉案清單', icon: 'reply', permission: PermissionCode.MenuCaseTransfer },
      },
      {
        path: 'case/AlreadyToAppropriationToDoList',
        name: 'AlreadyToAppropriationToDoList',
        component: () => import('../views/case/AlreadyToAppropriationToDoList.vue'), // 請換成正確的組件
        meta: {
          title: '簽約完成待撥款',
          icon: 'contact',
          permission: PermissionCode.MenuCaseAppropriationPending,
        },
      },
      //status=70
      {
        path: 'case/AlreadyToAppropriationList',
        name: 'AlreadyToAppropriationList',
        component: () => import('../views/case/AlreadyToAppropriationList.vue'),
        meta: {
          title: '已送撥款',
          icon: 'contact',
          permission: PermissionCode.MenuCaseAppropriation,
        },
      },
      //status=72
      {
        path: 'case/AlreadyAccountingList',
        name: 'AlreadyAccountingList',
        component: () => import('../views/case/AlreadyAccountingList.vue'), // 請換成正確的組件
        meta: {
          title: '撥款完成',
          icon: 'contact',
          permission: PermissionCode.MenuCaseAccounting,
        },
      },
      //status=67
      {
        path: 'case/SupplementList',
        name: 'SupplementList',
        component: () => import('../views/case/SupplementList.vue'), // 請換成正確的組件
        meta: { title: '補件', icon: 'contact', permission: PermissionCode.MenuCaseSupplement },
      },

      //status=68
      {
        path: 'case/SupplementReturnList',
        name: 'SupplementReturnList',
        component: () => import('../views/case/SupplementReturnList.vue'), // 請換成正確的組件
        meta: {
          title: '補件退回',
          icon: 'contact',
          permission: PermissionCode.MenuCaseSupplementReturn,
        },
      },

      //status=71
      {
        path: 'case/ElectronicContractReturnList',
        name: 'ElectronicContractReturnList',
        component: () => import('../views/case/ElectronicContractReturnList.vue'),
        meta: {
          title: '電子合約退回-待確認',
          icon: 'contact',
          permission: PermissionCode.MenuCaseAppropriationReturn,
        },
      },
      {
        path: 'case/AppropriationReturnList',
        name: 'AppropriationReturnList',
        component: () => import('../views/case/AppropriationReturnList.vue'), // 請換成正確的組件
        meta: {
          title: '撥款退回',
          icon: 'contact',
          permission: PermissionCode.MenuCaseAppropriationReturn,
        },
      },

      {
        path: 'case/inquery',
        name: 'InqueryList',
        component: () => import('../views/case/InqueryList.vue'),
        meta: { title: '案件查詢', icon: 'search', permission: PermissionCode.CaseSearch },
      },

      {
        path: 'report/daily-website-case-summary',
        name: 'DailyWebsiteCaseSummaryReport',
        component: () => import('../views/report/DailyWebsiteCaseSummaryReportView.vue'),
        meta: {
          title: '官網每日案件總表',
          icon: 'chart',
          menuGroup: '統計報表',
          permission: PermissionCode.MenuReportDailyWebsiteCaseSummary,
        },
      },
      {
        path: 'report/daily-sales-conversion',
        name: 'DailySalesConversionReport',
        component: () => import('../views/report/DailySalesConversionReportView.vue'),
        meta: {
          title: '官網流量統計',
          icon: 'chart',
          menuGroup: '統計報表',
          permission: PermissionCode.MenuReportPerformance,
        },
      },
      {
        path: 'downloads',
        name: 'DownloadCenter',
        component: () => import('../views/DownloadCenterView.vue'),
        meta: {
          title: '報表下載',
          icon: 'download',
          menuGroup: '下載專區',
          permission: PermissionCode.MenuDownloadCenter,
        },
      },
      {
        path: 'report/daily-website-traffic',
        name: 'DailyWebsiteTrafficReport',
        component: () => import('../views/report/DailyWebsiteTrafficReportView.vue'),
        meta: {
          title: '官網每日來客流量',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailyWebsiteTraffic,
        },
      },
      {
        path: 'report/daily-case-trend',
        name: 'DailyCaseTrendReport',
        component: () => import('../views/report/DailyCaseTrendReportView.vue'),
        meta: {
          title: '官網每日業務案件進度',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailyTrend,
        },
      },
      {
        path: 'report/daily-pending-case-trend',
        name: 'DailyPendingCaseTrendReport',
        component: () => import('../views/report/DailyPendingCaseTrendReportView.vue'),
        meta: {
          title: '官網每日暫存案件進度',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailyPendingCaseTrend,
        },
      },
      {
        path: 'report/daily-customer-submission-trend',
        name: 'DailyCustomerSubmissionTrendReport',
        component: () => import('../views/report/DailyCustomerSubmissionTrendReportView.vue'),
        meta: {
          title: '每日客戶送件案件進度',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailyCustomerSubmissionTrend,
        },
      },
      {
        path: 'report/daily-sales-submission-trend',
        name: 'DailySalesSubmissionTrendReport',
        component: () => import('../views/report/DailySalesSubmissionTrendReportView.vue'),
        meta: {
          title: '每日業務送件進度',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailySalesSubmissionTrend,
        },
      },
      {
        path: 'report/daily-review-submission-trend',
        name: 'DailyReviewSubmissionTrendReport',
        component: () => import('../views/report/DailyReviewSubmissionTrendReportView.vue'),
        meta: {
          title: '每日進審查案件進度',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportDailyReviewSubmissionTrend,
        },
      },
      {
        path: 'report/performance',
        name: 'PerformanceReport',
        component: () => import('../views/report/PerformanceReportView.vue'),
        meta: {
          title: '業務績效追蹤報表',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportPerformance,
        },
      },
      {
        path: 'report/appropriation-performance',
        name: 'AppropriationPerformanceReport',
        component: () => import('../views/report/AppropriationPerformanceReportView.vue'),
        meta: {
          title: '業務撥款報表',
          icon: 'chart',
          menuGroup: '統計報表',
          permission: PermissionCode.MenuReportPerformance,
        },
      },
      {
        path: 'report/sales-case-tracking',
        name: 'SalesCaseTrackingReport',
        component: () => import('../views/report/SalesCaseTrackingReportView.vue'),
        meta: {
          title: '業務人員案件追蹤表',
          icon: 'chart',
          menuGroup: '統計報表',
          permission: PermissionCode.MenuReportMarketing,
        },
      },
      {
        path: 'report/temporary-marketing',
        name: 'TemporaryMarketingReport',
        component: () => import('../views/report/TemporaryMarketingReportView.vue'),
        meta: {
          title: '暫存行銷統計報表',
          icon: 'chart',
          menuGroup: '統計報表',
          permission: PermissionCode.MenuReportMarketing,
        },
      },
      {
        path: 'report/daily-submission-flow',
        name: 'DailySubmissionFlowReport',
        component: () => import('../views/report/DailySubmissionFlowReportView.vue'),
        meta: {
          title: '每日送件流程統計',
          icon: 'chart',
          hiddenInMenu: true,
          permission: PermissionCode.MenuReportSubmissionFlow,
        },
      },
      {
        path: 'report/realtime-dashboard',
        name: 'RealtimeDashboardReport',
        component: () => import('../views/report/RealtimeDashboardReportView.vue'),
        meta: {
          title: '即時案件戰報',
          icon: 'chart',
          menuGroup: '管理報表',
          permission: PermissionCode.MenuReportRealtime,
        },
      },

      //--- 系統管理模組 (系統管理員 Admin 權限) ---
      {
        path: 'report/sales-lead-daily',
        name: 'SalesLeadDailyReport',
        component: () => import('../views/report/SalesLeadDailyReportView.vue'),
        meta: {
          title: '業務名單（日報）',
          permissions: [PermissionCode.MenuWebToContact, PermissionCode.MenuReportMarketing],
          roleCodes: [RoleCode.HosinSales, RoleCode.HosinManager, RoleCode.HosinOwner],
          menuGroup: '業務報表',
        },
      },
      {
        path: 'report/sales-appropriation',
        name: 'SalesAppropriationReport',
        component: () => import('../views/report/SalesAppropriationReportView.vue'),
        meta: {
          title: '業務撥款業績報表',
          icon: 'chart',
          permission: PermissionCode.MenuReportSalesAppropriation,
          menuGroup: '業務報表',
        },
      },
      {
        path: 'report/sales-performance',
        name: 'SalesPerformanceReport',
        component: () => import('../views/report/SalesPerformanceReportView.vue'),
        meta: {
          title: '業務績效追蹤報表',
          icon: 'chart',
          permission: PermissionCode.MenuReportSalesPerformance,
          menuGroup: '業務報表',
        },
      },
      {
        path: 'admin/rosters',
        name: 'RosterManagement',
        component: () => import('../views/admin/RosterManagement.vue'),
        meta: {
          title: '業務派件管理',
          icon: 'people',
          permission: PermissionCode.MenuRosterManagement,
          menuGroup: '系統維護',
        },
      },
      {
        path: 'admin/users',
        name: 'UserManagement',
        component: () => import('../views/admin/UserManagement.vue'),
        meta: {
          title: '使用者管理',
          icon: 'users',
          permission: PermissionCode.MenuAdminUsers,
          menuGroup: '系統維護',
        },
      },
      {
        path: 'admin/permissions',
        name: 'PermissionSettings',
        component: () => import('../views/admin/PermissionSettings.vue'),
        meta: {
          title: '角色與功能設定',
          icon: 'sliders',
          permission: PermissionCode.MenuAdminPermissions,
          menuGroup: '系統維護',
        },
      },
      {
        path: 'admin/contact-result-codes',
        name: 'ContactResultCodeManagement',
        component: () => import('../views/admin/ContactResultCodeManagement.vue'),
        meta: {
          title: '聯絡結果代碼維護',
          icon: 'list-check',
          permission: PermissionCode.MenuAdminContactResultCodes,
          menuGroup: '系統維護',
        },
      },

      {
        path: 'admin/uatImport',
        name: 'UatImportView',
        component: () => import('../views/admin/UatImportView.vue'),
        meta: {
          title: 'UAT 測試資料匯入',
          icon: 'file-earmark-excel',
          permission: PermissionCode.MenuAdminUat,
          menuGroup: '系統維護',
        },
      },

      {
        path: 'admin/uatAddCase/:id?',
        name: 'UatImportView',
        component: () => import('../views/admin/UatAddCase.vue'),
        meta: {
          title: 'UAT 模擬官網進件',
          icon: 'file-earmark-plus',
          permission: PermissionCode.MenuAdminUat,
          menuGroup: '系統維護',
        },
      },

      // // --- 業務管理模組 ---
      // {
      //   path: 'sales/TodoList',
      //   name: 'SalesTodoList', // 避免與其他 TodoList 撞名
      //   component: () => import('../views/sales/TodoList.vue'),
      //   meta: { title: '待進件清單', icon: 'list', systemRoles: ['Dealer', 'Hosin', 'Admin'] }
      // }
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// 🌟 導航守衛：權限攔截核心
router.beforeEach(async (to) => {
  let token = localStorage.getItem('token')
  const userStore = useUserStore() // 確保在 beforeEach 內呼叫
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  // 1. 驗證是否已登入
  if (requiresAuth) {
    if (!token) {
      return { name: 'Login', query: { redirect: to.fullPath } }
    }

    token = await ensureAccessToken()
    if (!token) {
      userStore.logout()
      alert('您的登入狀態已過期，請重新登入。')
      return { name: 'Login', query: { redirect: to.fullPath } }
    }
    userStore.syncFromStorage()
  } else if (to.name === 'Login' && token) {
    const activeToken = await ensureAccessToken()
    if (activeToken) {
      userStore.syncFromStorage()
      return getDefaultAuthorizedRoute(userStore.userInfo)
    }
    userStore.logout()
  }

  const requiredPermissions =
    (to.meta.permissions as string[] | undefined) ??
    (to.meta.permission
      ? [to.meta.permission as string]
      : to.meta.systemRoles
        ? [PermissionCode.CaseList]
        : [])
  if (requiredPermissions.some((permission) => disabledReportPermissions.includes(permission))) {
    return getDefaultAuthorizedRoute(userStore.userInfo)
  }
  if (requiredPermissions.length > 0) {
    if (!requiredPermissions.some((permission) => userStore.hasPermission(permission))) {
      console.warn(`[Router Guard] 權限不足，拒絕訪問 ${String(to.name)}`)
      alert('您沒有權限存取此頁面。')
      return getDefaultAuthorizedRoute(userStore.userInfo)
    }
  }
  const allowedRoleCodes = to.meta.roleCodes as string[] | undefined
  if (allowedRoleCodes && (!userStore.roleCode || !allowedRoleCodes.includes(userStore.roleCode))) {
    return userStore.hasPermission(PermissionCode.CaseList)
      ? { name: 'Dashboard' }
      : { name: 'Login' }
  }

  return true
})

export default router
